import { useApolloClient } from '@apollo/client'
import useParseOpeningHours from '../utils/getOpeningHours.utils'
import { BookingGetLotQuery, BookingGetLotQueryVariables } from 'parkdepot-shared/gql/graphql'
import { useCallback, useEffect, useState } from 'react'
import { GET_ALL_PUBLIC_LOTS } from '../graphql/queries/getAllPublicLots.query'
import { GET_LOTS_BY_LAT_LON } from '../graphql/queries/getLots.query'
import { GET_LOT_WITH_ID } from '../graphql/queries/getLotWithId.query'
import { Lot, Schedule } from '../types/lot.interface'
import { MapLocation } from '../types/mapLocation.interface'
import { getCleanCityName } from '@utils/getCleanCityName.utils'

export const useFetchLots = (searchedLocation: MapLocation | null = null) => {
    const client = useApolloClient()
    const [displayedLotIds, setDisplayedLotIds] = useState<string[]>([])
    const [loading, setLoading] = useState<boolean>(true)
    const { getDateOpeningHour, openingHours } = useParseOpeningHours()
    const [publicLots, setPublicLots] = useState<{ id: string; latitude: number; longitude: number }[]>([])

    const splitAddress = useCallback((address: string): { address: string; city: string } => {
        const addressArray = address.split(', ')
        return { address: addressArray[0], city: addressArray[1] }
    }, [])

    const fetchLotData = async (lotId: string) => {
        // fetch lot data
        try {
            const result = await client.query<BookingGetLotQuery, BookingGetLotQueryVariables>({
                query: GET_LOT_WITH_ID,
                variables: {
                    lot_id: lotId
                }
            })

            if (result.data && result.data.booking_get_lot) {
                const lot = result.data.booking_get_lot
                const publicLot = publicLots.find((publicLot) => publicLot.id === lot.id)
                const openingHoursObject = openingHours(lot.opening_hours.opening_hours_rrules as Schedule[])
                const maxNumber = getDateOpeningHour(new Date(), openingHoursObject)
                return {
                    ...lot,
                    latitude: publicLot?.latitude || 0,
                    longitude: publicLot?.longitude || 0,
                    ...splitAddress(lot.address),
                    disabled: maxNumber === 0,
                    cityName: getCleanCityName(splitAddress(lot.address).city)
                } as unknown as Lot
            } else {
                return null
            }
        } catch (error) {
            return null
        }
    }

    useEffect(() => {
        setLoading(true)
        const getPublicLots = async () => {
            try {
                const result = await client.query<any>({
                    // TODO: Check this
                    query: GET_ALL_PUBLIC_LOTS
                })
                const publicLots = result.data.booking_get_public_lots.lots.map(
                    (lot: { id: string; latitude: number; longitude: number }) => ({
                        ...lot
                    })
                )
                setPublicLots(publicLots)
                setDisplayedLotIds(publicLots.map((lot: { id: string }) => lot.id))
            } catch (error) {
                // console.error(error)
            } finally {
                setLoading(false)
            }
        }

        const fetchLotsWithLocation = async (lat: number, lng: number) => {
            try {
                const lots = await client.query({
                    query: GET_LOTS_BY_LAT_LON,
                    variables: {
                        latitude: lat || 47.2692,
                        longitude: lng || 11.4041
                    }
                })
                const lotIds = lots.data.booking_get_lots.map((lot: Lot) => lot.id)
                setPublicLots(lots.data.booking_get_lots)
                setDisplayedLotIds(lotIds)
            } catch (error) {
                // console.error(error)
            } finally {
                setLoading(false)
            }
        }

        // get url parameters
        const urlParams = new URLSearchParams(window.location.search)
        const lat = urlParams.get('lat')
        const lng = urlParams.get('lng')

        setPublicLots([])
        setDisplayedLotIds([])
        if ((lat && lng) || searchedLocation !== null) {
            const location = searchedLocation || { lat: parseFloat(lat || '0'), lng: parseFloat(lng || '0') }
            fetchLotsWithLocation(location.lat, location.lng)
        } else {
            getPublicLots()
        }
    }, [client, searchedLocation])

    return { publicLots, loading, displayedLotIds, fetchLotData }
}
